import React from 'react';
import autoBind from 'react-autobind';
import Localization from "../Localization.jsx";
import Config from "../Config.jsx";
import { Modal, SimpleGrid, Card, Text, Group, Button, Box, RingProgress } from '@mantine/core';
import {
  IconDatabase,
  IconCopy,
  IconUserShield,
  IconUser,
  IconCurrentLocation,
  IconEye,
  IconWorld,
  IconMap,
  IconBell,
  IconCloudOff,
  IconCategory,
  IconListNumbers,
  IconBooks,
  IconFolder,
  IconInfoCircle,
  IconDownload,
  IconLayoutGrid,
  IconCloudUpload,
  IconStopwatch,
  IconRoute,
  IconVideo,
  IconShare,
  IconMicrophone,
  IconChecklist,
  IconBellRinging,
  IconMapPin,
  IconCube,
  IconRobot,
} from '@tabler/icons-react';

const iconProps = { size: 40, stroke: 1.5, color: 'var(--mantine-color-gray-6)' };
const PLAN_EXTRA_LOCALE = 'PLAN_EXTRA_LOCALE';

function formatFileSize(bytes) {
  if (bytes == null || isNaN(bytes) || bytes === 0) return '0 B';
  const num = Number(bytes);
  if (num < 1024) return num + ' B';
  if (num < 1024 * 1024) return (num / 1024).toFixed(1) + ' KB';
  if (num < 1024 * 1024 * 1024) return (num / (1024 * 1024)).toFixed(2) + ' MB';
  return (num / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

function formatSubscriptionPeriod(period) {
  if (!period) return '';
  const start = period.date_start || '';
  const expire = period.date_expire || '';

  if (start && expire) {
    return `${start} - ${expire}`;
  }

  return start || expire || '';
}

function RingProgressWithLabel({ value, color }) {
  const v = value >= 100 ? 100 : value;
  const isError = color === 'error';
  return (
    <RingProgress
      size={80}
      thickness={6}
      roundCaps
      sections={[{ value: v, color: isError ? 'red' : 'brand' }]}
      label={
        <Text size="xs" ta="center" c={isError ? 'red' : 'dimmed'}>
          {`${Math.ceil(value)}%`}
        </Text>
      }
    />
  );
}

// Choose an icon that best represents the privilege
function getPrivilegeIcon(privilege) {
  const sourceEn = `${privilege?.privilege_name || ''} ${privilege?.privilege_name_long_en || ''}`.toUpperCase();
  const sourceZh = `${privilege?.privilege_name_long_zh || ''}`;
  const privilegeKey = `${privilege?.privilege_name || ''}`.toUpperCase();

  if (privilegeKey === PLAN_EXTRA_LOCALE) {
    return <IconCopy {...iconProps} />;
  }

  if (privilegeKey === 'AI_NPC_CONTENT_TYPE' || sourceEn.includes('NPC')) {
    return <IconRobot {...iconProps} />;
  }

  if (privilegeKey === 'AI_3D_MODEL' || sourceEn.includes('3D MODEL')) {
    return <IconCube {...iconProps} />;
  }

  // High-priority mappings for specific Chinese labels (unique icons)
  if (sourceZh.includes('離線') && sourceZh.includes('類型')) return <IconLayoutGrid {...iconProps} />; // 離線LOCALE戶外教材類型數量
  if (sourceZh.includes('離線') && (sourceZh.includes('數量') || sourceZh.includes('數'))) return <IconDownload {...iconProps} />; // 離線LOCALE戶外教材數量
  if (sourceZh.includes('高解析度') || (sourceZh.includes('照片') && sourceZh.includes('上傳'))) return <IconCloudUpload {...iconProps} />; // 允許高解析度照片上傳
  if (sourceZh.includes('語音回應時間') || sourceZh.includes('回應時間')) return <IconStopwatch {...iconProps} />; // 允許最大語音回應時間(秒)
  if (sourceZh.includes('路徑記錄')) return <IconRoute {...iconProps} />; // 允許用戶路徑記錄
  if (sourceZh.includes('嵌入') && sourceZh.includes('影片')) return <IconVideo {...iconProps} />; // 允許在LOCALE戶外教材中嵌入影片
  if (sourceZh.includes('答案') && (sourceZh.includes('匯出') || sourceZh.includes('導出'))) return <IconShare {...iconProps} />; // 允許答案匯出
  if (sourceZh.includes('語音轉文字')) return <IconMicrophone {...iconProps} />; // 啟用AI語音轉文字
  if (sourceZh.includes('自動評估')) return <IconChecklist {...iconProps} />; // 啟用AI自動評估
  if (sourceZh.includes('進度通知')) return <IconBellRinging {...iconProps} />; // 啟用AI進度通知
  if (sourceZh.includes('位置通知')) return <IconMapPin {...iconProps} />; // 位置通知 → location pin

  // English/general keyword fallbacks
  if (sourceEn.includes('STORAGE')) return <IconDatabase {...iconProps} />;
  if (sourceEn.includes('COPY')) return <IconCopy {...iconProps} />;
  if (sourceEn.includes('ADMIN')) return <IconUserShield {...iconProps} />;
  if (sourceEn.includes('TEACHER') || sourceEn.includes('ACCOUNT')) return <IconUser {...iconProps} />;
  if (sourceEn.includes('GPS')) return <IconCurrentLocation {...iconProps} />;
  if (sourceEn.includes('LOCATION') && (sourceEn.includes('SHOW') || sourceEn.includes('DISPLAY'))) return <IconEye {...iconProps} />;
  if (sourceEn.includes('WORLD')) return <IconWorld {...iconProps} />;
  if (sourceEn.includes('MAP')) return <IconMap {...iconProps} />;
  if (sourceEn.includes('NOTIFICATION')) return <IconBell {...iconProps} />;
  if (sourceEn.includes('OFFLINE')) return <IconCloudOff {...iconProps} />;
  if (sourceEn.includes('TYPE')) return <IconCategory {...iconProps} />;
  if (sourceEn.includes('NUMBER') || sourceEn.includes('QUANTITY') || sourceEn.includes('AMOUNT')) return <IconListNumbers {...iconProps} />;
  if (sourceEn.includes('LOCALE')) return <IconBooks {...iconProps} />;
  if (sourceEn.includes('PACKAGE') || sourceEn.includes('E-PACK')) return <IconFolder {...iconProps} />;

  return <IconInfoCircle {...iconProps} />;
}

class SubscriptionDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ai3dQuota: null,
      ai3dQuotaFetched: false,
    };
    autoBind(this);
  }

  componentDidUpdate(prevProps) {
    const { open, root } = this.props;
    if (open && !this.state.ai3dQuotaFetched && root && root.refs && root.refs.API) {
      const schoolId = typeof root.getMySchoolId === 'function' ? root.getMySchoolId() : null;
      if (schoolId) {
        root.refs.API.request(
          "get_ai_3d_quota",
          { school_id: schoolId },
          (res) => {
            const payload = res && res.body ? (res.body.result || res.body) : null;
            this.setState({ ai3dQuota: payload, ai3dQuotaFetched: true });
          },
          () => {
            this.setState({ ai3dQuotaFetched: true });
          }
        );
      } else {
        this.setState({ ai3dQuotaFetched: true });
      }
    }
  }

  render() {
    const { open, onClose, root } = this.props;

    if (!root || !root.refs || !root.refs.userDataProfile) {
      return null;
    }

    const {
      subscriptionPlan,
      subscriptionPlanNames,
      subscriptionPeriods,
      subscriptionInfo,
      school_storage_used,
      epack_created
    } = root.refs.userDataProfile.state;
    const subscriptionPeriodText = Array.isArray(subscriptionPeriods)
      ? subscriptionPeriods
        .map((period) => {
          const dateRange = formatSubscriptionPeriod(period);
          if (!dateRange) {
            return '';
          }

          return subscriptionPlanNames && subscriptionPlanNames.length > 1 && period.subscription_name
            ? `${period.subscription_name}: ${dateRange}`
            : dateRange;
        })
        .filter(Boolean)
        .join(', ')
      : '';

    return (
      <Modal
        opened={open}
        onClose={onClose}
        title={<Text fw={600}>{Localization.strings.SUBSCRIPTION_DETAILS}</Text>}
        size="85%"
        centered
        zIndex={2000}
      >
        <Box>
          <Group align="center" mb="lg">
            <Box>
              <Text fw={600} size="lg">
                {Localization.strings.PLAN}: {subscriptionPlan} {subscriptionPlanNames && Array.isArray(subscriptionPlanNames) ? `(${subscriptionPlanNames.join(', ')})` : ''}
              </Text>
              {subscriptionPeriodText && (
                <Text size="sm" c="dimmed" mt={4}>
                  {Localization.strings.SUBSCRIPTION_PERIOD || 'Subscription Period'}: {subscriptionPeriodText}
                </Text>
              )}
            </Box>
            {subscriptionPlan !== 'Premium' && (
              <Button>Upgrade</Button>
            )}
          </Group>

          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
            {Array.isArray(subscriptionInfo) && subscriptionInfo.map((privilege, index) => {
              const privKey = `${privilege.privilege_name || ''}`.toUpperCase();
              const is3D = privKey === 'AI_3D_MODEL';
              const isNPC = privKey === 'AI_NPC_CONTENT_TYPE';
              const descZhOverride = is3D
                ? (Localization.strings.HOTSPOT_CONTENT_3D_MODEL_DESCRIPTION || '3D模型')
                : (isNPC ? (Localization.strings.NPC_AVATAR_DESCRIPTION || 'NPC 虛擬角色') : null);
              const descEnOverride = is3D
                ? (Localization.strings.HOTSPOT_CONTENT_3D_MODEL_DESCRIPTION || '3D Model')
                : (isNPC ? (Localization.strings.NPC_AVATAR_DESCRIPTION || 'NPC Avatar') : null);
              const displayDescription = Localization.strings.getLanguage() === 'en'
                ? (descEnOverride || privilege.description_en || privilege.privilege_name_long_en)
                : (descZhOverride || privilege.description_zh || privilege.privilege_name_long_zh);

              return (
                <Card key={index} withBorder padding="md" style={{ height: '100%', minHeight: 180, display: 'flex', flexDirection: 'column' }}>
                  {/* Top visual area: either progress for special cases or a representative icon */}
                  <Group justify="center" mb="xs">
                    {privilege.privilege_name === 'STORAGE_LIMIT' && (() => {
                      const storageUsedBytes = school_storage_used * 1024;
                      const storageLimitBytes = Number(privilege.value) * 1024 * 1024;
                      const percentage = storageLimitBytes > 0 ? (storageUsedBytes / storageLimitBytes) * 100 : 0;
                      const color = percentage > 100 ? 'error' : 'primary';
                      return <RingProgressWithLabel value={percentage} color={color} />;
                    })()}
                    {privilege.privilege_name === 'MAX_LOCALE_NUMBER' && (() => {
                      const percentage = privilege.value > 0 ? (epack_created / privilege.value) * 100 : 0;
                      const color = percentage > 100 ? 'error' : 'primary';
                      return <RingProgressWithLabel value={percentage} color={color} />;
                    })()}
                    {privilege.privilege_name !== 'STORAGE_LIMIT' && privilege.privilege_name !== 'MAX_LOCALE_NUMBER' && (
                      getPrivilegeIcon(privilege)
                    )}
                  </Group>

                  {/* Description */}
                  <Text ta="center" mb={4} style={{ wordBreak: 'break-word' }}>
                    {displayDescription}
                  </Text>

                  {/* Bottom status/value area */}
                  <Group justify="center" align="center" mt="auto" style={{ minHeight: 28 }}>
                    {(() => {
                      // Boolean type
                      if (privilege.value_type === '1') {
                        const isSupported = `${privilege.value}` === '1' || `${privilege.value}`.toLowerCase() === 'true';
                        const showQuota = privKey === 'AI_3D_MODEL';
                        const quotaValue = (showQuota && this.state.ai3dQuota && (this.state.ai3dQuota.AI_3D_quotas !== undefined))
                          ? this.state.ai3dQuota.AI_3D_quotas
                          : privilege.value_2;
                        const quotaText = isSupported && showQuota && quotaValue !== undefined && quotaValue !== null
                          ? ` (${Localization.strings.REMAINING_QUOTA || 'Remaining quota'}: ${quotaValue})`
                          : '';
                        return (
                          <Text ta="center" fw={700} c={isSupported ? 'green' : 'red'}>
                            {isSupported ? `${Localization.strings.SUPPORTED}${quotaText}` : Localization.strings.NOT_SUPPORTED}
                          </Text>
                        );
                      }

                      // Numeric or other types
                      if (privilege.privilege_name === 'STORAGE_LIMIT') {
                        const storageUsedBytes = school_storage_used * 1024;
                        const storageLimitBytes = Number(privilege.value) * 1024 * 1024;
                        const percentage = storageLimitBytes > 0 ? (storageUsedBytes / storageLimitBytes) * 100 : 0;
                        const overLimit = percentage > 100;
                        return (
                          <Text ta="center" fw={700} c={overLimit ? 'red' : undefined}>
                            {`${formatFileSize(storageUsedBytes)} / ${(Number(privilege.value) / 1024).toFixed(2)} GB`}
                          </Text>
                        );
                      }

                      if (privilege.privilege_name === 'MAX_LOCALE_NUMBER') {
                        return (
                          <Text ta="center" fw={700}>
                            {`${epack_created} / ${privilege.value}`}
                          </Text>
                        );
                      }

                      if (privKey === PLAN_EXTRA_LOCALE) {
                        const template = Localization.strings.PLAN_EXTRA_LOCALE_VALUE || '{0} extra LOCALE(s)';
                        return (
                          <Text ta="center" fw={700}>
                            {template.replace('{0}', privilege.value ?? 0)}
                          </Text>
                        );
                      }

                      return (
                        <Text ta="center" fw={700}>
                          {privilege.value}
                        </Text>
                      );
                    })()}
                  </Group>
                </Card>
              );
            })}
          </SimpleGrid>

          <Group justify="flex-end" mt="lg">
            <Button onClick={onClose}>
              {Localization.strings.CLOSE}
            </Button>
          </Group>
        </Box>
      </Modal>
    );
  }
}

export default SubscriptionDialog;
